import React, { useState, useRef, useEffect, useCallback } from 'react';
import MagicButton from 'components/MagicButton';
import { useGenerateSeoReportMutation, useGenerateFullReportMutation } from 'store/services/admin/adminSeoApi';
import { toast } from 'react-toastify';
import './RunAnalysisButton.css';

const PROGRESS_STEPS = [
  'Fetching GSC data...',
  'Analyzing competitors...',
  'Building content plan...',
  'Done!',
];

const RunAnalysisButton: React.FC = () => {
  const [generateQuick, { isLoading: isQuickLoading }] = useGenerateSeoReportMutation();
  const [generateFull, { isLoading: isFullLoading }] = useGenerateFullReportMutation();

  const [showDropdown, setShowDropdown] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const isRunning = isQuickLoading || isFullLoading;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  // Click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const handleQuick = async () => {
    setShowDropdown(false);
    try {
      const result = await generateQuick().unwrap();
      if (result && 'status' in result && (result as Record<string, unknown>).status === 'error') {
        toast.error((result as Record<string, unknown>).message as string || 'Failed to generate report');
        return;
      }
      toast.success('Gap zone report updated');
    } catch (err: unknown) {
      const message = (err && typeof err === 'object' && 'data' in err)
        ? String((err as { data: { message?: string } }).data?.message || 'Failed to generate report')
        : 'Failed to generate report';
      toast.error(message);
    }
  };

  const handleFull = async () => {
    setShowDropdown(false);
    clearTimers();
    setProgressStep(0);

    // Simulated progress steps
    timersRef.current.push(setTimeout(() => setProgressStep(1), 8000));
    timersRef.current.push(setTimeout(() => setProgressStep(2), 20000));

    try {
      const result = await generateFull().unwrap();
      clearTimers();
      if (result && 'status' in result && (result as Record<string, unknown>).status === 'error') {
        toast.error((result as Record<string, unknown>).message as string || 'Full analysis failed');
        setProgressStep(-1);
        return;
      }
      setProgressStep(3); // Done!
      toast.success('Full analysis completed');
      setTimeout(() => setProgressStep(-1), 3000);
    } catch (err: unknown) {
      clearTimers();
      setProgressStep(-1);
      const message = (err && typeof err === 'object' && 'data' in err)
        ? String((err as { data: { message?: string } }).data?.message || 'Full analysis failed')
        : 'Full analysis failed';
      toast.error(message);
    }
  };

  return (
    <div className="run-analysis-container" ref={dropdownRef}>
      <MagicButton
        label={isQuickLoading ? 'Updating...' : isFullLoading ? 'Analyzing...' : 'Run Analysis'}
        onPress={() => setShowDropdown(prev => !prev)}
        color="purple"
        isLoading={isRunning}
        isDisabled={isRunning}
      />

      {showDropdown && !isRunning && (
        <div className="run-analysis-dropdown">
          <button className="run-analysis-option" onClick={handleQuick}>
            <span className="run-option-label">Quick Update</span>
            <span className="run-option-desc">GSC data only (~5s)</span>
          </button>
          <button className="run-analysis-option" onClick={handleFull}>
            <span className="run-option-label">Full Analysis</span>
            <span className="run-option-desc">GSC + competitors + content plan (~60s)</span>
          </button>
        </div>
      )}

      {progressStep >= 0 && (
        <div className="run-progress-steps">
          {PROGRESS_STEPS.map((label, idx) => (
            <div
              key={idx}
              className={`run-progress-step ${
                idx < progressStep ? 'step-done' :
                idx === progressStep ? 'step-active' : 'step-pending'
              }`}
            >
              <span className="step-icon">
                {idx < progressStep ? '✓' : idx === progressStep ? '●' : '○'}
              </span>
              <span className="step-label">{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RunAnalysisButton;
